import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button as Btn } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";

import { useTranslation } from "contexts/TranslationContext";
import routes, { appRoute } from "shared/constants/routes";
import { getServicesByCategory, OTHER_SERVICES_CATEGORY } from "./otherServicesData";
import OtherServiceCard from "./OtherServiceCard";
import ComingSoonModal from "./ComingSoonModal";
import "./OtherServices.css";

// Home "Other Services" section: a left header column (title + subtitle + prev/
// next arrows) beside a horizontally scrollable track of service cards, with a
// centered "View All" button below (→ the full list page with tabs).
//
// The Home carousel surfaces the "Other Services" bucket; "Vehicle Services"
// live behind the second tab on the View All page. Clicking any card opens the
// Coming Soon modal (these services aren't wired to a flow yet).
const OtherServices = () => {
  const { translate, language } = useTranslation();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const isRtl = language?.dir === "rtl";

  const items = getServicesByCategory(OTHER_SERVICES_CATEGORY.OTHER);

  // Scroll the track by ~80% of its visible width. Sign is flipped in RTL so
  // "next" always advances toward the not-yet-seen cards regardless of direction.
  const scrollByViewport = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: (isRtl ? -direction : direction) * amount, behavior: "smooth" });
  };

  const handleViewAll = () => navigate(appRoute(routes.OTHER_SERVICES + "/list"));

  // Data-driven: services flagged comingSoon open the modal; the rest navigate
  // to their detail page.
  const handleServiceClick = (item) => {
    if (item?.comingSoon) {
      setComingSoonOpen(true);
    } else {
      navigate(appRoute(routes.OTHER_SERVICES + `/${item.id}`));
    }
  };

  return (
    <div className="rounded-xl mb-4 px-4 py-6 md:py-8 bg-white">
      <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-10">
        {/* Header column */}
        <div className="lg:w-1/4 shrink-0">
          <h2 className="text-3xl md:text-4xl font-bold text-[#212044] mb-3">
            {translate("OTHER_SERVICES.TITLE")}
          </h2>
          <p className="text-[#8E9196] text-sm md:text-base mb-6 max-w-xs">
            {translate("OTHER_SERVICES.SUBTITLE")}
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              aria-label="Previous"
              onClick={() => scrollByViewport(-1)}
              className="w-11 h-11 rounded-full border border-[#C8CDD2] text-[#8E9196] flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
            >
              {isRtl ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={() => scrollByViewport(1)}
              className="w-11 h-11 rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
            >
              {isRtl ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
            </button>
          </div>
        </div>

        {/* Scrollable card track */}
        {/* min-w-0 lets this flex child scroll instead of expanding the row (and
            the page) to fit its overflowing content. */}
        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto scroll-smooth hide-scrollbar flex-1 min-w-0 pb-2"
        >
          {items.map((item) => (
            <div
              key={item.id}
              className="min-w-[240px] sm:min-w-[260px] md:min-w-[280px] max-w-[280px]"
            >
              <OtherServiceCard item={item} onClick={handleServiceClick} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center mt-10">
        <Btn
          className="rounded-full bg-primary text-white font-semibold p-5 w-36 border border-primary hover:!border-primary hover:border-solid hover:!text-primary"
          onClick={handleViewAll}
        >
          {translate("HOME.VIEW_ALL") || "View All"}
        </Btn>
      </div>

      <ComingSoonModal open={comingSoonOpen} onClose={() => setComingSoonOpen(false)} />
    </div>
  );
};

export default OtherServices;
